import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { permissionErrorModalSingleton } from '@/api/permissionErrorModalSingleton.util';
import { CustomDialog } from '@/components/dialog/CustomDialog.comp';
import { CustomDialogTypeEnum } from '@/components/dialog/model/CustomDialogTypeEnum.enum';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';

import PermissionErrorModalContext from './PermissionErrorModal.context';

type Props = {
  children: React.ReactNode;
};

export const PermissionErrorModalProvider: React.FC<Props> = ({ children }) => {
  const { t } = useTranslationWithPrefix('errors');
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const showPermissionError = useCallback((message?: string) => {
    setErrorMessage(message || '');
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setErrorMessage('');
  }, []);

  // Register the showPermissionError function in the singleton
  useEffect(() => {
    permissionErrorModalSingleton.register(showPermissionError);
    return () => {
      permissionErrorModalSingleton.unregister();
    };
  }, [showPermissionError]);

  const dialogContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
        <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 40 }} />
        <Typography sx={{ flex: 1 }} variant="body1">
          {errorMessage || t('permissionError.message')}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <PermissionErrorModalContext.Provider value={{ showPermissionError }}>
      {children}
      <CustomDialog
        confirmButtonColor="error"
        confirmButtonText={t('permissionError.button')}
        dialogContent={dialogContent}
        dialogTitle={t('permissionError.title')}
        hideCancelButton
        isOpen={isOpen}
        onClose={handleClose}
        onConfirm={handleClose}
        type={CustomDialogTypeEnum.Warning}
      />
    </PermissionErrorModalContext.Provider>
  );
};
